import businessDays from 'business-days-js';
import { JSNumberToDayMapping, JSNumberToMonthFullNameMapping, JSNumberToMonthMapping, PleasantFeelings, UnpleasantFeelings } from "../../Constants";
import { getDateTimeFromId } from "../EntriesHelper";
import { generateResponseFromGPT } from "../../adapters/insights/gencontent-adapter";
import { getPeakValleyDescriptionFromGPT } from './PeakValleyHelper';

const businessDaysUS = businessDays({ excludeHolidays: ["columbus day", "veterans day"] });

/**
 * @param flows UI objects for flowentries
 * @param option Can be set to be 'work' OR 'life' to filter only work or life flows by their pleasantness value. 
 * By default option is null to return the total pleasant/unpleasant distribution across work and life flows combined
 * @returns An object with 2 properties: pleasant and unpleasant. Each prop's value is an array of filtered flows.
 */
export function getFlowsByPleasantnessValue(flows, option = null) {
    if (!flows) {
        return { pleasant: [], unpleasant: [] }
    }

    let totalPleasantFlows = [];
    let totalUnpleasantFlows = [];
    flows.forEach(flow => {
        if (flow.color.name.toLowerCase().includes('green')) {
            totalPleasantFlows.push(flow);
        }
        else {
            totalUnpleasantFlows.push(flow);
        }
    });

    if (option === 'work') {
        // filter out work flows in each category
        let workPleasantFlows = totalPleasantFlows.filter(flow => flow.isDirectlyWorkRelated === true);
        let workUnpleasantFlows = totalUnpleasantFlows.filter(flow => flow.isDirectlyWorkRelated === true);
        return { 'pleasant': workPleasantFlows, 'unpleasant': workUnpleasantFlows };
    }
    else if (option === 'life') {
        let lifePleasantFlows = totalPleasantFlows.filter(flow => flow.isDirectlyWorkRelated === false);
        let lifeUnpleasantFlows = totalUnpleasantFlows.filter(flow => flow.isDirectlyWorkRelated === false);
        return { 'pleasant': lifePleasantFlows, 'unpleasant': lifeUnpleasantFlows };
    }
    else {
        return { 'pleasant': totalPleasantFlows, 'unpleasant': totalUnpleasantFlows };
    }
}

/**
 * This function compares two numbers and returns whether the result is net negative, positive, or neutral
 * @param {number} numPleasant - Non-negative number to represent how many pleasant flows. 
 * @param {number} numUnpleasant - Non-negative number to represent how many unpleasant flows. 
 * @returns {Object} An object like this { net: 'positive', difference: 4 }
 */
export function calculateNetValue(numPleasant, numUnpleasant) {
    const difference = Math.abs(numPleasant - numUnpleasant);

    let net = 'neutral';
    if (numPleasant > numUnpleasant) {
        net = 'positive';
    }
    else if (numUnpleasant > numPleasant) {
        net = 'negative';
    }

    return { net, difference };
}

/**
 * @param arrayOfTuples e.g. looks like [['x', 3], ['y', 3], ['z', 2]]
 * @param threshold Tuples with count greater or equal to this number will be returned. By default it's null and that means
 * the tuples with the highest count in the array will be returned.
 * @returns an array of tuple(s) where count is max, so for the example array it would return [['x', 3], ['y', 3]]
 */
export function getHighestCountTuples(arrayOfTuples, threshold = null) {
    if (!arrayOfTuples || arrayOfTuples.length === 0) {
        return [];
    }
    // If no threshold is passed in, set the value to be the highest count that exists among all tuples in the array
    if (!threshold) {
        threshold = arrayOfTuples.reduce(
            (max, tuple) => Math.max(max, tuple[1]), // the callback function to keep track of max so far
            -Infinity // initial value of max set to the smallest thing possible
        );
    }

    // Step 2: Filter elements whose count is greater or equal to max
    return arrayOfTuples.filter(tuple => tuple[1] >= threshold);
}

/**
 * @param arrayOfTuples e.g. looks like [['x', 3], ['y', 2], ['z', 2]]
 * @param threshold Tuples with count less or equal to this number will be returned. By default it's null and that means
 * the tuples with the lowest count in the array will be returned.
 * @returns an array of tuple(s) where count is min, so for the example array it would return [['y', 2], ['z', 2]]
 */
export function getLowestCountTuples(arrayOfTuples, threshold = null) {
    if (!arrayOfTuples || arrayOfTuples.length === 0) {
        return [];
    }
    // If no threshold is passed in, set the value to be the highest count that exists among all tuples in the array
    if (!threshold) {
        threshold = arrayOfTuples.reduce(
            (min, tuple) => Math.min(min, tuple[1]),
            Infinity // initial value of min set to the largest thing possible
        );
    }
    // Step 2: Filter elements whose count is less or equal to min
    return arrayOfTuples.filter(tuple => tuple[1] <= threshold);
}

/**
 * @param {Date} weekStartDate 
 * @returns {object} { 
 * startDate: start date of week (A sunday), 
 * endDate: end date of the week (a saturday),
 * week1: a string describing which week of the month it is, like "August - Week 4",
 * week2: Present if the first/last week of the month overlaps with prev/next month. The string rep is a bit shorter like "Jan Week 5"
 */
export function getInfoAboutWeekInMonth(weekStartDate) {
    // given a week start date, determine what number week it is in month.
    let info = {
        startDate: weekStartDate,
        endDate: new Date(weekStartDate.getFullYear(), weekStartDate.getMonth(), weekStartDate.getDate() + 6, 23, 59, 59),
        week1: null,
        week2: null
    };
    const year = weekStartDate.getFullYear();
    const month = weekStartDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    // Get the date corresponding to the first Sunday this month.
    const numDaysUntilFirstSunday = firstDayOfMonth.getDay() === 0 ? 0 : 7 - firstDayOfMonth.getDay();
    const firstSundayOfMonth = new Date(year, month, 1 + numDaysUntilFirstSunday);

    // which week does the month's first sunday fall into?
    let weekNumFirstSunday;
    if (numDaysUntilFirstSunday < 3) {
        // This means there is no eligible partial week in this month before its first sunday.
        // so this first sunday begins the week 1.
        weekNumFirstSunday = 1;
    }
    else {
        // there is an eligible partial week before the first sunday.
        weekNumFirstSunday = 2;
    }

    // week start date is always a sunday, so which week are we on then?
    const deltaFromFirstSunday = getNumDaysInBetween(firstSundayOfMonth, weekStartDate);
    const weekNum = Math.floor(deltaFromFirstSunday / 7) + weekNumFirstSunday;
    info.week1 = `${JSNumberToMonthFullNameMapping[month]} - Week ${weekNum}`;

    // if weekStart falls on the last sunday of the month?
    const lastDateOfMonth = new Date(year, month + 1, 0);
    // how far is the month end from last sunday in month
    const monthEndOffsetFromSunday = lastDateOfMonth.getDay();
    const lastSundayDateNum = lastDateOfMonth.getDate() - monthEndOffsetFromSunday;
    const nextMonth = month < 10 ? month + 1 : 0;
    if (weekStartDate.getDate() === lastSundayDateNum) {
        if (monthEndOffsetFromSunday >= 2 && monthEndOffsetFromSunday < 4) {
            info.week1 = `${JSNumberToMonthMapping[month]} Week ${weekNum}`;
            info.week2 = `${JSNumberToMonthMapping[nextMonth]} Week 1`
        }
        else if (monthEndOffsetFromSunday < 2) {
            // this becomes part of next month's week 1
            info.week1 = `${JSNumberToMonthFullNameMapping[nextMonth]} - Week 1`
        }
    }

    return info;
}

export function getNumDaysInBetween(startDate, endDate) {
    // standardize dates to UTC so business days library doesn't break on daylight savings transition days
    const startDateInUTC = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDateInUTC = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    return businessDaysUS.countDays(startDateInUTC, endDateInUTC).totalDays;
}

/**
 * Returns the number of weeks present in a month. We determine that by taking into account:
 * 1) full weeks in a month - aka Sun-Sat periods where all 6 days lie in the given month.
 * 2) Any eligible partial weeks at the start/end (with atleast 3 days of the month).
 * For e.g March 2025 is considered to have 4 weeks. April 2025 has 5. 
 * @param {number} year Like 2023 
 * @param {number} month number for a month as per the 0-11 index
 * @returns 
 */
export function getNumWeeksInMonth(year, month) {
    const firstDateOfMonth = new Date(year, month, 1);
    const lastDateOfMonth = new Date(year, month + 1, 0);
    const totalDaysInMonth = lastDateOfMonth.getDate();

    const firstDayOfMonth = firstDateOfMonth.getDay();
    const firstSundayOffset = firstDayOfMonth === 0 ? 0 : 7 - firstDayOfMonth;
    const firstSundayDateNum = 1 + firstSundayOffset;

    let weeks = 0;
    // start by counting the number of sundays in the month
    for (let i = firstSundayDateNum; i <= totalDaysInMonth; i = i + 7) {
        weeks += 1;
    }
    // check for eligible partial week at the start.
    if (firstSundayOffset > 2) {
        weeks += 1;
    }

    const lastSundayOffset = lastDateOfMonth.getDay(); // how far is last sunday from the end of the month
    const lastSundayDateNum = totalDaysInMonth - lastSundayOffset;
    // Check the last sunday and update count if it is not part of an eligible partial week.
    if (totalDaysInMonth - lastSundayDateNum < 2) {
        weeks -= 1;
    }
    return weeks;
}

/**
 * Given an array of flows, get the percentage and number of flows that are pleasant and unpleasant
 * @returns {object|undefined} An object like this {pleasant: { numFlows: 1, percentage: 25 }, unpleasant: { numFlows: 3, percentage: 75 }}.
 * Or undefined if there are no flows.
 */
export function getPleasantUnpleasantPercentages(flows) {
    const counter = {
        pleasant: { numFlows: 0, percentage: 0 },
        unpleasant: { numFlows: 0, percentage: 0 }
    };

    if (!flows || flows.length === 0) return;

    const flowsByPleasantnessValue = getFlowsByPleasantnessValue(flows);

    counter.pleasant.numFlows = flowsByPleasantnessValue.pleasant.length;
    counter.pleasant.percentage = Math.round((counter.pleasant.numFlows / flows.length) * 100);

    counter.unpleasant.numFlows = flowsByPleasantnessValue.unpleasant.length;
    counter.unpleasant.percentage = 100 - counter.pleasant.percentage;

    return counter;
}

/** 
 * Calculates the difference in total flow counts between the current week and the previous week.
 * @returns A number. Positive numbers indicate more flows this week and negative numbers indicate more flows last week. 
 */
export function getFlowCountDiff(previousWeekFlows, currentWeekFlows) {
    return currentWeekFlows.length - previousWeekFlows.length
}

/**
 * Get the pleasant percentage difference between current and previous week
 * @returns {number|undefined} percentage number, eg. 30%. Positive means increase in pleasantness this week, negative means decrease, and 0 means no change.
 * If there are no flows in either current or previous week, return undefined because diff is not applicable.
 */
export function getPleasantPercentDiff(previousWeekFlows, currentWeekFlows) {
    if ((!previousWeekFlows || previousWeekFlows.length === 0) || (!currentWeekFlows || currentWeekFlows.length === 0)) {
        return;
    }
    const previousWeek = getPleasantUnpleasantPercentages(previousWeekFlows);
    const currentWeek = getPleasantUnpleasantPercentages(currentWeekFlows);

    return currentWeek.pleasant.percentage - previousWeek.pleasant.percentage;
}

/**
 * Get the net values, eg. (positive, negative, neutral), for work flows AND life flows
 * @returns An object like this - {work: { net: 'positive', difference: 4 }, life { net: 'negative', difference: 1 }}
 */
export function getWorkLifeNetValues(flows) {
    const workFlows = getFlowsByPleasantnessValue(flows, 'work');
    const lifeFlows = getFlowsByPleasantnessValue(flows, 'life');

    const life = lifeFlows.pleasant.length === 0 && lifeFlows.unpleasant.length === 0
        ? null
        : {
            net: calculateNetValue(
                lifeFlows.pleasant.length,
                lifeFlows.unpleasant.length
            ).net,
            numPleasant: lifeFlows.pleasant.length,
            numUnpleasant: lifeFlows.unpleasant.length,
        };

    let pleasantWorkInquiry = 0;
    let unpleasantWorkInquiry = 0;
    // get additional pleasant unpleasant numbers about work from life flows if they talked about it
    if (life) {
        flows.forEach((flow) => {
            if (flow.isWorkFacing) {
                const workInquiryAnswer = flow.entry.answeredQuestions.find(question => question.category === "work_inquiry")?.answer;
                if (PleasantFeelings.includes(workInquiryAnswer)) {
                    pleasantWorkInquiry++;
                }
                if (UnpleasantFeelings.includes(workInquiryAnswer)) {
                    unpleasantWorkInquiry++;
                }
            }
        })
    }

    const numWorkPleasant = workFlows.pleasant.length + pleasantWorkInquiry;
    const numWorkUnpleasant = workFlows.unpleasant.length + unpleasantWorkInquiry;
    const work = numWorkPleasant === 0 && numWorkUnpleasant === 0
        ? null
        : {
            net: calculateNetValue(numWorkPleasant, numWorkUnpleasant).net,
            numPleasant: numWorkPleasant,
            numUnpleasant: numWorkUnpleasant
        };

    if (!work && !life) {
        return;
    }
    else {
        return { 
            component: 'workLifeNetValues', 
            data: { work, life },
            duration: 7000
        };
    }
}

/**
 * Gets rewards and their counts, sorted by least to most.
 * @returns An array of arrays like [['Habit Hero', 1], ['Consistent', 2], ['Sweet Savorer', 3]]. 
 * Empty array if no flows or rewards found.
 */
export function getRewardCounts(flows) {
    if (!flows || flows.length === 0) {
        return [];
    }

    let tally = {};
    for (const flow of flows) {
        if (flow.entry?.qualitativeRewards) {
            flow.entry.qualitativeRewards.forEach(reward => {
                tally[reward] = (tally[reward] || 0) + 1;
            });
        }
        if (flow.entry?.quantitativeRewards) {
            flow.entry.quantitativeRewards.forEach(reward => {
                tally[reward] = (tally[reward] || 0) + 1;
            })
        }
    }
    const rewardsSortedByCount = Object.entries(tally).sort((a, b) => a[1] - b[1]);
    return rewardsSortedByCount;
}

/**
 * @param {Array} flows Flows of the week
 * @param {Array} sortedPeakAndValleyOccurences This tells you the order (via indices) in which peaks and valleys happened. Used for one of the gpt calls made
 * @returns {Array} An array of objects like {targetedComponent: <string>, text: <string response from gpt}>}, where
 * the targeted component is the name of the component that needs to be displaying the gpt text
 */
export async function getStoryGPTData(flows, sortedPeakAndValleyOccurences, targetedComponents) {
    const componentRequestMap = {
        energyTrend: getEnergyLevelsTrend(flows),
        pleasantnessTrend: getPleasantnessLevelsTrend(flows),
        peakValley: getPeakValleyDescriptionFromGPT(sortedPeakAndValleyOccurences)
    }

    const requests = targetedComponents.map(name => componentRequestMap[name]);

    const responses = await Promise.allSettled(requests);

    return responses.map((response, index) => ({
        targetedComponent: targetedComponents[index],
        text: response.status === 'fulfilled' ? response.value : null
    }));
}

/**
 * @param {Array} flows for a week
 * @returns {object|null} An object that looks like: 
 * {
 *  data: An array of objects containing the date a flow happened and the energy level
 *  trend: A string description of the trend of energy levels through a week, as generated by gpt. If gpt's response in not ok then this value is null.
 * }
 * Returns null when there are less than 3 flows passed in.
 */
export async function getEnergyLevelsTrend(flows) {
    if (!flows || flows.length < 3) {
        return null;
    }
    try {
        const dataForGPT = flows.map(flow => ({
            'day': getDateTimeFromId(flow.entry._id).split(', ')[1],
            'energy': flow.entry.answeredQuestions[0].answer
        }))
        const doesDataHaveDrained = dataForGPT.find(element => element.energy === 'Drained') === undefined ? false : true;
        const prompt = doesDataHaveDrained ?
            `I have an array of objects representing my energy levels from last week. Each object has the energy level and the day/time corresponding to it. 
            Did you spot any patterns trends in how energy levels changed throughout the week. Pay special attention to drained energy levels. 
            Answer in a conversational tone, past tense, and no more than 24 words. 
            Do not include any greetings, confirmations, or lead-ins—just the pattern or trend.
            Do not make suggestions or show concern.
            Here's the data for reference: ${JSON.stringify(dataForGPT)}`
            :
            `I have an array of objects representing my energy levels from last week. Each object has the energy level and the day/time corresponding to it. 
            Did you spot any patterns trends in how energy levels changed throughout the week.
            Answer in a conversational tone, past tense, and no more than 24 words. 
            Do not include any greetings, confirmations, or lead-ins—just the pattern or trend.
            Do not make suggestions or show concern.
            Here's the data for reference: ${JSON.stringify(dataForGPT)}`;

        const response = await generateResponseFromGPT(prompt);
        if (!response || !response.ok) {
            return null;
        }
        const trend = await response.json();
        // remove any quote marks around words
        return trend.replace(/["'](\w+)["']/g, "$1");
    }
    catch (error) {
        console.error(error);
        return null;
    }
}

/**
 * @param {Array} flows for a week
 * @returns {Array|null} An array of objects with this shape {date: Date(), energy: String}
 * Returns null when there are less than 3 flows passed in.
 */
export function getEnergyLevelsChartData(flows) {
    if (!flows || flows.length < 3) {
        return null;
    }

    const dataForD3 = flows.map(flow => ({
        'date': flow.date,
        'energy': flow.entry.answeredQuestions[0].answer.toLowerCase()
    }));

    return dataForD3;
}

/**
 * @param {Array} flows for a week
 * @returns {string|null} A description from GPT of how pleasantness levels changed through the week.
 */
export async function getPleasantnessLevelsTrend(flows) {
    if (!flows || flows.length < 3) {
        return null;
    }
    try {
        const dataForGPT = flows.map(flow => {
            const result = {
                date: getDateTimeFromId(flow.entry._id).split(', ')[1],
                feeling: undefined
            }
            if (flow.entry.answeredQuestions[0].answer === 'Drained') {
                result.feeling = "drained"
                return result;
            }
            const feelingQuestion = flow.entry.answeredQuestions.find(q => q.category === "feeling");
            if (feelingQuestion.answer) {
                if (feelingQuestion.answer === "Alright") {
                    result.feeling = flow.color.name.toLowerCase().includes('green') ? "alright" : "alright, but slightly unpleasant";
                }
                else {
                    result.feeling = feelingQuestion.answer.toLowerCase();
                }
            }

            return result.feeling ? result : undefined;
        });

        const prompt = `I have an array of objects representing how I was feeling last week. Each object has the feeling and the day/time corresponding to it. 
        Consider week start to be from Sunday to Tuesday afternoon, week mid to be Tuesday eve to Thursday night, and week end to be Friday and Saturday. 
        How would you then describe the week start, mid and end? 
        Were any times more correlated with a certain mood?
        Answer in a kind tone, past tense, and no more than 24 words. 
        Do not include any greetings, confirmations, or lead-ins—just the pattern or trend.
        Do not use quotation marks or slashes anywhere.
        Skip mentioning mood correlations if none are clear—do not say anything about it.
        Here's the data for reference: ${JSON.stringify(dataForGPT)}`;

        const response = await generateResponseFromGPT(prompt);
        if (!response || !response.ok) {
            return null;
        }
        const trend = await response.json();
        const regex = /banana/i;
        if (regex.test(trend)) {
            return null;
        }
        // remove any quote marks around words
        return trend.replace(/["'](\w+)["']/g, "$1")
    }
    catch (error) {
        console.error(error);
        return null;
    }
}

/**
 * @returns {string} A description from GPT for the given data and prompt.
 */
export async function getDescriptionFromGPT(data, prompt) {
    if (!data || data.length < 1) {
        return null;
    }

    const fullPrompt = `${prompt}. Here's the data for reference: ${JSON.stringify(data)}`;

    const response = await generateResponseFromGPT(fullPrompt);
    if (!response || !response.ok) {
        return null;
    }
    const trend = await response.json();
    // remove any quote marks around words
    return trend.replace(/["'](\w+)["']/g, "$1")
}

/**
 * @param {Date} datetime
 * @returns {string} Returns a string corresponding to the input datetime like 'Monday afternoon', 'Saturday late night' etc.
 */
export function getSimplifiedDayTime(datetime) {
    if (!(datetime instanceof Date) || isNaN(datetime)) {
        return '';
    }

    const hours = datetime.getHours();
    let timeOfDay;
    let dayIndex = datetime.getDay(); // Default to current day

    if (hours >= 0 && hours < 3) {
        timeOfDay = "late night";
        dayIndex = (dayIndex - 1 + 7) % 7; // Move to previous day
    }
    else if (hours >= 3 && hours < 7) {
        timeOfDay = "early morning";
    }
    else if (hours >= 7 && hours < 12) {
        timeOfDay = "morning";
    }
    else if (hours >= 12 && hours < 15) {
        timeOfDay = "afternoon";
    }
    else if (hours >= 15 && hours <= 16) {
        timeOfDay = "late afternoon";
    }
    else if (hours >= 17 && hours < 20) {
        timeOfDay = "eve";
    }
    else if (hours >= 20 && hours < 23) {
        timeOfDay = "night";
    }
    else {
        timeOfDay = "late night";
    }

    return `${JSNumberToDayMapping[dayIndex].Full} ${timeOfDay}`;
}

/**
 * @param {Date} date This will be the week start date
 * @returns {string} Returns "YYYY-MM-DD" string corresponding to the given date which can then be stored in the DB
 */
export function formatDateForStorage(date) {
    return date.getFullYear() +
        "-" + String(date.getMonth() + 1).padStart(2, '0') +
        "-" + String(date.getDate()).padStart(2, '0');
}

/**
 * @param {string} dateStr In "YYYY-MM-DD" format that's stored in the db 
 * @returns {Date} Returns the date object corresponding to the string in local time
 */
export function parseDateFromStorageString(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day); // Month is 0-based
}

/**
 * From gpt. Includes min, excludes max. 
 */
export function getRandomNumber(min, max) {
    return Math.floor(Math.random() * (max - min)) + min;
}

/**
 * @param {Date} weekStart Sunday of the week
 * @returns {boolean} True if the week end date (Saturday) was more than 48 hours ago.
 */
export function isWeekInThePast(weekStart) {
    // Sat 11:59pm of the week
    const weekEndDate = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6, 23, 59, 59);
    const diffInMilliSecs = Math.abs(new Date() - weekEndDate); // Absolute difference in milliseconds
    return diffInMilliSecs > 48 * 60 * 60 * 1000; // 48 hours in milliseconds
}